// More sheet (mobile overhaul Phase 0 §0.4.4; mockup §1.2 D). Every tab the
// bottom bar doesn't hold, in the sidebar's own order and groups, with the
// sidebar's badges — so the mental model is the same on both devices. The
// sheet appends a System group of its own (sidebarNav.ts has none):
// Automations, Theme, Settings.

import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { mergeSidebarLayout, useSidebarLayout } from '../lib/sidebarLayout';
import {
  DEFAULT_SIDEBAR_LAYOUT,
  SIDEBAR_GROUP_LABELS,
  SIDEBAR_NAV_ITEMS,
} from '../lib/sidebarNav';
import { SIDEBAR_GROUPS, type SidebarGroup } from '../../shared/prefs';
import { Sheet, SheetButton, SheetRow, SheetSection } from './Sheet';
import {
  MAutomationsIcon,
  MClientsIcon,
  MCrewIcon,
  MDashboardsIcon,
  MFinanceIcon,
  MLeadsIcon,
  MMoonIcon,
  MOutreachIcon,
  MProjectsIcon,
  MScriptsIcon,
  MSettingsIcon,
  MSocialIcon,
  MDocumentsIcon,
  MStatsIcon,
  MTasksIcon,
  SearchIcon,
} from './icons';
import { hasMobileWorkspace } from '../lib/toolEmbed';
import type { Badges } from './Shell';

/** The bar already holds these — Overview isn't in any sidebar group. */
const BAR_TABS = new Set(['/tasks', '/leads', '/projects']);

/** Collapsed into one expandable row so the sheet stays on one screen
    (mockup §1.2 D note 2). Order inside the group is preserved. */
const COLLAPSED_TOOLS = ['/tools/reviews', '/tools/media'];

/** 24-grid mobile twins of the sidebar icons; anything without one falls
    back to the item's own 16-grid icon. */
const MOBILE_ICONS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  '/leads': MLeadsIcon,
  '/outreach': MOutreachIcon,
  '/projects': MProjectsIcon,
  '/tasks': MTasksIcon,
  '/clients': MClientsIcon,
  '/crew': MCrewIcon,
  '/finance': MFinanceIcon,
  '/stats': MStatsIcon,
  '/social': MSocialIcon,
  '/documents': MDocumentsIcon,
  '/dashboards': MDashboardsIcon,
  '/tools/scripts': MScriptsIcon,
};

export default function MoreSheet({
  open,
  onClose,
  badges,
  themeLabel,
  onToggleTheme,
  onSearch,
}: {
  open: boolean;
  onClose: () => void;
  badges: Badges | null;
  /** "Auto" / "Dark" / "Light" — Shell owns the theme state. */
  themeLabel: string;
  onToggleTheme: () => void;
  onSearch: () => void;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const sidebarPref = useSidebarLayout();
  const layout = mergeSidebarLayout(sidebarPref, DEFAULT_SIDEBAR_LAYOUT);
  const [toolsExpanded, setToolsExpanded] = useState(false);

  // Reopening the sheet starts collapsed again — the expanded state is a
  // one-off detour, not a preference.
  useEffect(() => {
    if (!open) setToolsExpanded(false);
  }, [open]);

  if (!open) return null;

  const go = (to: string) => {
    navigate(to, { replace: to === pathname });
    onClose();
  };

  const badgeFor = (id: string) => {
    const item = SIDEBAR_NAV_ITEMS[id];
    const count = item.badge && badges ? badges[item.badge] : 0;
    if (!count) return undefined;
    return <span className={`mrow-bd${item.alert ? '' : ' plain'}`}>{count}</span>;
  };

  const navRow = (id: string) => {
    const item = SIDEBAR_NAV_ITEMS[id];
    const Icon = MOBILE_ICONS[id] ?? item.icon;
    return (
      <SheetRow
        key={id}
        icon={<Icon />}
        label={item.label}
        sub={item.tool ? (hasMobileWorkspace(item.to.split('/').pop() ?? '') ? 'Edit on mobile' : 'Best on desktop') : undefined}
        right={item.tool ? <span className="mrow-ext">↗</span> : badgeFor(id)}
        onSelect={() => go(item.to)}
      />
    );
  };

  const renderGroup = (key: SidebarGroup) => {
    const ids = layout[key].filter((id) => !BAR_TABS.has(id));
    if (ids.length === 0) return null;
    const collapsed = ids.filter((id) => COLLAPSED_TOOLS.includes(id));
    let emittedCollapsed = false;
    return (
      <div key={key}>
        <SheetSection>{SIDEBAR_GROUP_LABELS[key]}</SheetSection>
        {ids.map((id) => {
          if (!collapsed.includes(id)) return navRow(id);
          if (emittedCollapsed) return null;
          emittedCollapsed = true;
          return (
            <div key="collapsed-tools">
              <SheetRow
                icon={<MScriptsIcon />}
                label={collapsed.map((toolId) => SIDEBAR_NAV_ITEMS[toolId].label).join(' · ')}
                sub="Best on desktop"
                right={<span className="mrow-ext">{toolsExpanded ? '▴' : '▾'}</span>}
                onSelect={() => setToolsExpanded((v) => !v)}
              />
              {toolsExpanded && collapsed.map(navRow)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Sheet
      open
      onClose={onClose}
      variant="half"
      title="More"
      rightAction={<SheetButton onClick={onClose}>Close</SheetButton>}
      className="more-sheet"
    >
      <SheetRow
        icon={<SearchIcon />}
        label="Search"
        right="⌘K"
        onSelect={onSearch}
      />
      {SIDEBAR_GROUPS.map(renderGroup)}
      <SheetSection>System</SheetSection>
      <SheetRow
        icon={<MAutomationsIcon />}
        label="Automations"
        onSelect={() => go('/automations')}
      />
      <SheetRow icon={<MMoonIcon />} label="Theme" right={themeLabel} onSelect={onToggleTheme} />
      <SheetRow icon={<MSettingsIcon />} label="Settings" onSelect={() => go('/settings')} />
    </Sheet>
  );
}
